import React, { useEffect, useState } from "react";
import {
  Modal,
  Input,
  Select,
  Button,
  Form,
  message,
  Space,
  Typography,
} from "antd";
import { CheckCircleTwoTone, CopyOutlined, PlusOutlined } from "@ant-design/icons";
import { useAppDispatch } from "../hooks/reduxHooks";
import { userInvitationApi } from "../services/users.service";

const { Option } = Select;

const InviteUserModal = ({
  isUserInviteModalVisible,
  setIsUserInviteModalVisible,
}) => {
  const [loading, setLoading] = useState(false);
  const [invitationURL, setInvitationURL] = useState("");
  const [isInvitationDialogOpen, setIsInvitationDialogOpen] = useState(false);
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();

  const { Text } = Typography;

  const roleOptions = ["admin", "agent"];
  const alphaNumericRegex = /^[a-zA-Z0-9]+$/;

  useEffect(() => {
    if (isUserInviteModalVisible) {
      form.resetFields();
    }
  }, [isUserInviteModalVisible, form]);

  const handleOk = (values) => {
    setLoading(true);
    dispatch(
      userInvitationApi({
        data: values,
        finalCallback: () => {
          setLoading(false);
        },
        successCallback: (response) => {
          if (response.success) {
            message.success(response?.msg || "Token Generated!");

            const generatedInvitationURL = response?.registration_token
              ? `${window.location.origin}/invitation?token=${response?.registration_token}&username=${values?.username}&role=${values?.role}`
              : "";
            setInvitationURL(generatedInvitationURL);
            setIsInvitationDialogOpen(true);
            setIsUserInviteModalVisible(false);
          } else {
            message.error(response?.msg || "Failed to invite user.");
          }
        },
        failureCallback: (error) => {},
      })
    );
  };

  const handleUsernameChange = () => {
    if (form.getFieldError("username").length > 0) {
      form.setFields([{ name: "username", errors: [] }]);
    }
  };

  const handleCopyInvitationLink = async () => {
    if (!invitationURL) return;
    try {
      await navigator.clipboard.writeText(invitationURL);
      message.success("Invitation link copied to clipboard.");
    } catch (error) {
      message.error("Failed to copy invitation link.");
    }
  };

  return (
    <div>
      <Modal
        title={
          <Space direction="vertical" size={2} style={{ display: "flex" }}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Invite User
            </Typography.Title>
            <Text type="secondary">
              Generate an invitation link for a new team member.
            </Text>
          </Space>
        }
        onCancel={() => setIsUserInviteModalVisible(false)}
        width={450}
        open={isUserInviteModalVisible}
        destroyOnClose
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleOk}
          validateTrigger="onSubmit"
        >
          <Form.Item
            label="Username"
            name="username"
            style={{ marginTop: "20px", marginBottom: "12px" }}
            rules={[
              { required: true, message: "Please input the username!" },
              {
                min: 3,
                message: "Username must be at least 3 characters.",
              },
              {
                pattern: alphaNumericRegex,
                message: "Username can contain only letters and numbers.",
              },
            ]}
            extra="Only letters and numbers are allowed."
          >
            <Input
              placeholder="Enter new user username"
              maxLength={50}
              onChange={handleUsernameChange}
            />
          </Form.Item>

          <Form.Item
            label="Role"
            name="role"
            rules={[{ required: true, message: "Please select a role!" }]}
            style={{ marginBottom: "32px" }}
          >
            <Select placeholder="Assign a role">
              {roleOptions.map((role) => (
                <Option key={role} value={role}>
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </Option>
              ))}
            </Select>
          </Form.Item>
          {/* Submit Button */}
          <Form.Item style={{ textAlign: "center" }}>
            <Button
              type="primary"
              htmlType="submit"
              icon={<PlusOutlined />}
              loading={loading}
              block
            >
              Generate Invite Link
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          <Space align="center" size={10}>
            {/* <CheckCircleTwoTone twoToneColor="#52c41a" /> */}
            <Typography.Title level={4} style={{ margin: 0 }}>
              Invitation Link
            </Typography.Title>
          </Space>
        }
        open={isInvitationDialogOpen}
        onCancel={() => setIsInvitationDialogOpen(false)}
        width={500}
        destroyOnClose
        footer={
          <Space style={{ display: "flex", justifyContent: "flex-end" }}>
            {/* <Button onClick={() => setIsInvitationDialogOpen(false)}>Close</Button> */}
            <Button type="primary" icon={<CopyOutlined />} onClick={handleCopyInvitationLink}>
              Copy Link
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Text type="secondary">
            Share this secure invitation link with the user to complete registration.
          </Text>
          <style>
            {`
              .invitation-card:hover {
                background-color: #f0f0f0 !important;
              }
            `}
          </style>
          <div
            className="invitation-card"
            style={{
              borderRadius: "10px",
              fontSize: "13px",
              backgroundColor: "#fafafa",
              border: "1px solid #d9d9d9",
              padding: "10px 12px",
              color: "rgba(0, 0, 0, 0.88)",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 5,
              WebkitBoxOrient: "vertical",
              wordBreak: "break-all",
              lineHeight: "1.5",
              minHeight: "calc(1.5em * 5 + 20px)",
              cursor: "pointer",
            }}
            title={"Click to copy invitation link"}
            onClick={handleCopyInvitationLink}
          >
            {invitationURL}
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default InviteUserModal;
